import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { API_BASE_URL } from '../../config/api'
import './OracleChatWelcome.css'

const REFRESH_MS = 5 * 60 * 1000
let snapshot = null
let expiresAt = 0
let pending = null
const loadPulse = () => {
  if (snapshot && Date.now() < expiresAt) return Promise.resolve(snapshot)
  if (!pending) {
    pending = (async () => {
      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), 25000)
      try {
        const response = await fetch(`${API_BASE_URL}/api/oracle/market-pulse`, { signal: controller.signal })
        if (!response.ok) throw new Error('Market feed unavailable')
        const data = await response.json()
        if (!data.success || !Array.isArray(data.items)) throw new Error('Invalid market feed')
        snapshot = data
        expiresAt = Date.now() + REFRESH_MS
        return data
      } finally {
        clearTimeout(timeout)
      }
    })().finally(() => { pending = null })
  }
  return pending
}

const useDesktop = () => {
  const [desktop, setDesktop] = useState(() => window.matchMedia('(min-width: 769px)').matches)
  useEffect(() => {
    const media = window.matchMedia('(min-width: 769px)')
    const change = () => setDesktop(media.matches)
    media.addEventListener('change', change)
    change()
    return () => media.removeEventListener('change', change)
  }, [])
  return desktop
}

export const OracleChatWelcome = ({ oracle, active = true, onSelect }) => {
  const { t, i18n } = useTranslation()
  const desktop = useDesktop()
  const [pulse, setPulse] = useState(null)
  const locale = i18n.resolvedLanguage || i18n.language || 'pt'
  const tr = (key, options) => t(`oracleWelcome.${key}`, options)

  useEffect(() => {
    if (!desktop || !active) return undefined
    let cancelled = false
    const refresh = () => {
      if (document.visibilityState === 'hidden') return
      loadPulse().then((data) => { if (!cancelled) setPulse(data) })
        .catch(() => { if (!cancelled) setPulse(null) })
    }
    refresh()
    const timer = setInterval(refresh, REFRESH_MS)
    document.addEventListener('visibilitychange', refresh)
    return () => { cancelled = true; clearInterval(timer); document.removeEventListener('visibilitychange', refresh) }
  }, [desktop, active])

  if (!desktop || !active) return null
  const items = (pulse?.items || []).filter((item) => Date.parse(item.startsAt) > Date.now()).slice(0, 3)
  const volume = pulse?.metric === 'volume'
  const number = (value) => new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value)
  const dollars = (value) => new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 }).format(value)

  return (
    <div className="oracle-chat-welcome">
      <h1>{tr(`questions.${oracle}`)}</h1>
      {items.length > 0 && <section className="oracle-market-pulse" aria-label={tr('marketLabel')}>
        <div className="oracle-market-pulse-heading">
          <span><i aria-hidden="true" />{tr(volume ? 'volumeHeading' : 'dropHeading')}</span>
          <small>{pulse.source} · {tr('nextGames')}</small>
        </div>
        <div className="oracle-market-pulse-items">
          {items.map((item) => <button type="button" key={item.id} onClick={() => onSelect(tr(volume ? `prompts.${oracle}` : 'dropPrompt', { match: item.title, market: item.market, outcome: item.outcome, bookmaker: item.bookmaker }))}>
            <span className="oracle-market-pulse-match">{item.title}</span>
            <span className="oracle-market-pulse-detail">{volume ? tr('totalVolume') : `${item.bookmaker} · ${item.market.replace(/_/g, ' ')} · ${item.outcome}`}</span>
            <strong className={volume ? 'is-volume' : 'is-drop'}>{volume ? dollars(item.volumeUsd) : `${number(item.dropPercent)}%`}</strong>
            <span className="oracle-market-pulse-action" aria-hidden="true">↗</span>
          </button>)}
        </div>
        <div className="oracle-market-pulse-time">{tr('updated')} <time dateTime={pulse.updatedAt}>{new Date(pulse.updatedAt).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}</time></div>
      </section>}
    </div>
  )
}

export const OracleChatBanner = () => {
  const { t } = useTranslation()
  const desktop = useDesktop()
  if (!desktop) return null
  return (
    <aside className="oracle-chat-banner" aria-label={t('oracleHome.quantumLabel')}>
      <div className="oracle-chat-banner-brand">
        <img src={`${process.env.PUBLIC_URL}/images/oracles/quantum_odds_logo.png`} alt="Quantum Odds" width="184" height="55" />
        <p>{t('oracleHome.quantumHeadline.0')}<br />{t('oracleHome.quantumHeadline.1')}</p>
      </div>
      <ul>{t('oracleHome.quantumFeatures', { returnObjects: true }).map((feature) => <li key={feature}>{feature}</li>)}</ul>
      <a href="https://quantumodds.xyz/" target="_blank" rel="noopener noreferrer">{t('oracleHome.quantumAction')} <span aria-hidden="true">→</span></a>
    </aside>
  )
}
